import React from 'react';

import tw from '@root/tailwind.config';
import { classNamesBy, getBaseClass, mapClassNamesToScreens } from 'components/Picture/utils';

interface breakpoints {
  base?: number | string;
  xs?: number | string;
  sm?: number | string;
  md?: number | string;
  lg?: number | string;
}

type PictureProps = {
  alt: string;
  className?: string;
  crop?: boolean;
  focalPointX?: number;
  focalPointY?: number;
  hero?: boolean;
  polaroid?: boolean;
  srcSet?: breakpoints;
  url: string;
  wpWidth?: number;
};

const Picture = ({
  alt,
  className = '',
  crop = true,
  focalPointX = 0.5,
  focalPointY = 0.3,
  hero = false,
  polaroid = false,
  srcSet,
  url,
  wpWidth,
}: PictureProps) => {
  const imgWidths: breakpoints = {
    base: hero ? 343 : srcSet && srcSet.base ? srcSet.base : 284,
    xs: hero ? 299 : srcSet && srcSet.xs ? srcSet.xs : 254,
    sm: hero ? 381 : srcSet && srcSet.sm ? srcSet.sm : 322,
    md: hero ? 436 : srcSet && srcSet.md ? srcSet.md : 358,
    lg: hero ? 580 : srcSet && srcSet.lg ? srcSet.lg : 376,
  };
  const polaroidClass = `polaroid`;
  const wordpressUrlParamaters = `ssl=1${wpWidth && `&resize=${wpWidth},${wpWidth / (7 / 5)}`}`;

  const screens: breakpoints = tw.theme.screens;
  const chunkedClasses = classNamesBy(className, 'aspect-ratio');
  const aspectRatios: breakpoints = mapClassNamesToScreens(screens, chunkedClasses.match, 'aspect-ratio-7by5');

  const hasValidAr = chunkedClasses.match.reduce((acc, val) => {
    const aspectRatio = val.split('-').slice(-1)[0].replace('by', ':');

    return aspectRatio && aspectRatio.replace(':', 'by') in tw.theme.aspectRatios ? acc : false;
  }, true);

  const showWarnings = !hasValidAr && process.env.ENVIRONMENT !== 'production';

  if (showWarnings)
    console.error(
      `Image uses an unsupported aspect ratio. Either edit the image to match one of the ratios already defined in the Tailwind theme file or define a new one.`,
    );

  const ImgixMessage = showWarnings
    ? 'monochrome=ffffffff&blur=600&blend-mode=lighten&blend64=YmJiYmJi&txt=Unsupported%20Aspect%20Ratio&txt-size=40&txt-color=ff0000&txt-align=middle,center&txt-font=Futura%20Condensed%20Medium'
    : '';

  const baseClass = getBaseClass(chunkedClasses.match.join(' '), 'aspect-ratio', 'aspect-ratio-7by5');
  const classWithBase = [...new Set([...chunkedClasses.match, baseClass])];

  return (
    <div className={`block w-full ${polaroid ? polaroidClass : ''} ${chunkedClasses.noMatch?.join(' ')}`}>
      <div className={wpWidth ? '' : classWithBase.join(' ')}>
        <picture className={`inline h-auto w-auto`}>
          {wpWidth ? (
            <img
              className={`block h-auto w-full max-w-full`}
              loading="lazy"
              alt={alt}
              src={`${url.split('?')[0]}?${wordpressUrlParamaters}`}
            />
          ) : (
            <>
              {Object.keys(aspectRatios)
                .reverse()
                .map((elm) => (
                  <source
                    key={elm}
                    media={elm === 'base' ? undefined : `(min-width: ${screens[elm as keyof breakpoints]})`}
                    srcSet={`${url}?fm=jpg${
                      crop ? '&fit=crop' : ''
                    }&crop=fp&fp-x=${focalPointX}&fp-y=${focalPointY}&ar=${aspectRatios[elm as keyof breakpoints]}&w=${
                      imgWidths[elm as keyof breakpoints]
                    }&auto=format&dpr=1&${showWarnings ? ImgixMessage : ''} 1x,
                  ${url}?fm=jpg${crop ? '&fit=crop' : ''}&crop=fp&fp-x=${focalPointX}&fp-y=${focalPointY}&ar=${
                    aspectRatios[elm as keyof breakpoints]
                  }&w=${imgWidths[elm as keyof breakpoints]}&auto=format&dpr=2&q=50&${showWarnings ? ImgixMessage : ''} 2x`}
                  />
                ))}

              <img className={`block h-auto w-full max-w-full`} loading="lazy" alt={alt} />
            </>
          )}
        </picture>
      </div>
    </div>
  );
};

export default Picture;
